import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import FileUploader from "components/FileUploader";

import { ID_DOCUMENT } from "statics/docTypes";
import Button from "components/Button";
import { intersection, map, noop, size } from "lodash";
import { docTypes } from "pages/signUp/individual/IndividualDocUploader";
import IconSpinner from "../../../../components/IconSpinner";

const acceptableNameChangeDocs = [
  docTypes.DRIVERS_LICENSE.name,
  docTypes.DRIVERS_PERMIT.name,
  docTypes.STATE_ID_CARD.name,
  docTypes.PASSPORT.name,
  docTypes.PASSPORT_CARD.name,
  docTypes.SSN_CARD.name,
  docTypes.MILITARY_ID.name,
  docTypes.MARRIAGE_CERTIFICATE.name,
  docTypes.DIVORCE_DECREE.name,
  docTypes.NAME_CHANGE_DOC.name,
  docTypes.TAX_W2.name,
];

const acceptableAddressChangeDocs = [
  docTypes.DRIVERS_LICENSE.name,
  docTypes.DRIVERS_PERMIT.name,
  docTypes.STATE_ID_CARD.name,
  docTypes.MILITARY_ID.name,
  docTypes.UTILITY_BILL.name,
  docTypes.LEASE.name,
  docTypes.MORTGAGE_AGREEMENT.name,
  docTypes.NYC_ID_CARD.name,
  docTypes.TUITION_STATEMENT.name,
];

const sharedDocs = intersection(
  acceptableNameChangeDocs,
  acceptableAddressChangeDocs
);

// The userInfo prop is a dead passthrough (never read in this component); it is
// forwarded by UserProfileWorkflow / AdminUserProfileWorkflow from their local
// profile state. Type it to the optional string fields those parents carry so
// the passthrough typechecks without an index-signature mismatch.
interface ProfileInfo {
  legalName?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phoneNumber?: string;
  email?: string;
}

// `docTypes` (IndividualDocUploader) is an object literal whose entries carry a
// mix of fields (some lack `numDocs`); reads here index it with dynamic string
// keys, so view it through this shared shape at each access. Mirrors the
// `docTypesByKey` pattern in IndividualDocsRequired.
type DocTypeInfo = { description: string; name: string; numDocs?: number };
const docTypesByKey = docTypes as Record<string, DocTypeInfo>;

interface Props {
  idDocType?: string;
  onSuccess?: () => void;
  isFrontUploader?: boolean;
  nameWasChanged?: boolean;
  addressWasChanged?: boolean;
  phoneWasChanged?: boolean;
  isBackUploader?: boolean;
  documents?: unknown[];
  goToNamedStep?: (name: string) => void;
  onSubmit?: () => void;
  // Passed by both consumers (UserProfileWorkflow / AdminUserProfileWorkflow);
  // `stepName` is read by the StepWizard parent, the others are forwarded.
  stepName?: string;
  userInfo?: ProfileInfo;
  isUpdating?: boolean;
}

interface State {
  selectedDocType: string;
}

export default class UploadDocumentation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedDocType: "",
    };
  }

  componentDidMount() {
    if (
      !this.props.nameWasChanged &&
      !this.props.addressWasChanged &&
      this.props.phoneWasChanged
    ) {
      // if only the phone number was changed, just update the profile and go back to the dashboard
      // PRESERVED BUG (conversion-only): onSubmit is not guaranteed by callers; `!` preserves throw-on-undefined
      this.props.onSubmit!();
    }
  }

  render() {
    const successMessage = "Your file was successfully uploaded.";
    let neededDocs;

    if (this.props.nameWasChanged && this.props.addressWasChanged) {
      neededDocs = sharedDocs;
    } else if (this.props.nameWasChanged) {
      neededDocs = acceptableNameChangeDocs;
    } else if (this.props.addressWasChanged) {
      neededDocs = acceptableAddressChangeDocs;
    }

    if (
      !this.props.nameWasChanged &&
      !this.props.addressWasChanged &&
      this.props.phoneWasChanged
    ) {
      return <IconSpinner centered />;
    }

    const buttonDisabled =
      !this.state.selectedDocType ||
      size(this.props.documents) <
        (docTypesByKey[this.state.selectedDocType].numDocs as number);

    return (
      <article className="col-form" style={{ paddingBottom: 0 }}>
        <Col
          as={Col}
          sm={8}
          style={{ paddingLeft: 0, paddingTop: 10, marginBottom: 12 }}
        >
          <article>
            <Form.Label>Document Requirements</Form.Label>
            <div className="processing-steps">
              <ul className="steps">
                <li>
                  Clear photos and PDFs are fine; we cannot accept screenshots
                  or scans.
                </li>
                <li>
                  All documents must be current. IDs cannot be expired, and
                  monthly bills or statements should have been issued within the
                  past 60 days.
                </li>
                <li>
                  We need to see all four corners of the document, and the
                  information we need to verify must be clearly visible.
                </li>
                <li>
                  If the document has a signature requirement, then it must be
                  hand-signed. We cannot accept electronic signatures (with the
                  exception of leases, where electronic signatures are
                  acceptable).
                </li>
                <li>
                  Government-issued documents (such as a state-issued ID or
                  passports) must be in color. We can accept black-and-white
                  photos or PDFs of other documents, such as utility bills.
                </li>
              </ul>
            </div>
          </article>
        </Col>
        <Row className="mb-3">
          <Form.Group
            as={Col}
            sm={6}
            controlId="formBasicSize"
            style={{ paddingLeft: 0, paddingBottom: 20 }}
          >
            <Form.Select
              name="documentType"
              defaultValue={""}
              value={this.state.selectedDocType}
              onChange={(e) =>
                this.setState({ selectedDocType: e.target.value })
              }
            >
              <option value="" disabled>
                Document Type
              </option>
              {map(neededDocs, (doc) => {
                return (
                  <option value={doc}>{docTypesByKey[doc].description}</option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          {this.state.selectedDocType && (
            <Form.Group as={Col} md={6} className="upload-card">
              <Form.Label className={"document-side"}>
                Front of Document:
              </Form.Label>
              <FileUploader
                fileLimit={1}
                docType={ID_DOCUMENT}
                subDocType={this.state.selectedDocType}
                onSuccess={noop}
                requireSubDocType={true}
                fileUploadedMsg={successMessage}
                hideUploadedFiles={true}
              />
            </Form.Group>
          )}
          {this.state.selectedDocType &&
            docTypesByKey[this.state.selectedDocType].numDocs === 2 && (
              <Form.Group as={Col} md={6} className="upload-card">
                <Form.Label className={"document-side"}>
                  Back of Document:
                </Form.Label>
                <FileUploader
                  fileLimit={1}
                  docType={ID_DOCUMENT}
                  subDocType={this.state.selectedDocType}
                  onSuccess={noop}
                  requireSubDocType={true}
                  fileUploadedMsg={successMessage}
                  hideUploadedFiles={true}
                />
              </Form.Group>
            )}
        </Row>
        <Row className="mb-3">
          <div className="btn-row">
            <Button
              size="sm"
              className="cancel-button"
              type="button"
              color="cancel"
              name="cancel"
              btnLabel="Go Back"
              onClick={() => {
                // PRESERVED BUG (conversion-only): goToNamedStep is injected by StepWizard but optional in the contract; `!` preserves throw-on-undefined
                this.props.goToNamedStep!("updateInfo");
              }}
            />
            <Button
              size="sm"
              withArrow={true}
              disabled={buttonDisabled}
              name="submit"
              btnLabel="Submit"
              onClick={this.props.onSubmit}
            />
          </div>
        </Row>
      </article>
    );
  }
}
